/**
 * Operator map-click redirect: lead (or last fix) → one tapped destination.
 *
 * The new line is applied the same way a Sentinel reroute is, so the phone
 * refreshes geometry and the `route_changed` stamp speaks on the navigator
 * (`REDIRECT_SPOKEN`).
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchPrimaryDirections } from '../routePlanner/api/routingApi';
import { sendCrewMessage } from '../../services/crewChatApi';
import {
  applyRoute,
  replaceMissionWaypoints,
  type Mission,
  type MissionDeviceState,
  type MissionRouteStatus,
} from '../../services/missionApi';
import type { MissionDeviceTrack } from './useMissionDeviceTracks';

export const REDIRECT_SPOKEN =
  'Emergency rerouting due to active threats or corridor restrictions';

export interface LatLng {
  longitude: number;
  latitude: number;
}

function hasFix(point?: { latitude?: number | null; longitude?: number | null } | null): boolean {
  return Number.isFinite(point?.latitude) && Number.isFinite(point?.longitude);
}

function fromDevice(device?: MissionDeviceState | null): LatLng | null {
  if (!device || !hasFix(device)) return null;
  return { longitude: device.longitude as number, latitude: device.latitude as number };
}

/** Where the convoy actually is, not where the plan started. */
export function convoyOrigin(
  status: MissionRouteStatus | null | undefined,
  tracks: MissionDeviceTrack[],
  waypoints?: Array<{ longitude: number; latitude: number }> | null,
): LatLng | null {
  // A fixed collection site always has a fresh fix and never moves — the
  // one device guaranteed to be the wrong answer to "where is the convoy".
  const moving = tracks.filter((t) => !t.isStationary);
  const leadId = status?.progress?.lead_device_id;
  const byTrackId = (id?: string | null) => (
    id ? moving.find((t) => t.deviceId === id && hasFix(t)) : undefined
  );
  const fromTrack = byTrackId(leadId)
    || moving.find((t) => hasFix(t));
  if (fromTrack) {
    return { longitude: fromTrack.longitude, latitude: fromTrack.latitude };
  }

  const devices = (status?.devices || []).filter((d) => !d.is_stationary);
  const fromState = fromDevice(leadId
    ? devices.find((d) => d.device_id === leadId)
    : undefined)
    || fromDevice(devices.find((d) => hasFix(d)));
  if (fromState) return fromState;

  const first = waypoints?.[0];
  if (first && hasFix(first)) {
    return { longitude: first.longitude, latitude: first.latitude };
  }
  return null;
}

function routeProfile(value?: string | null): 'driving-traffic' | 'driving' | 'walking' | 'cycling' {
  if (value === 'driving' || value === 'walking' || value === 'cycling') return value;
  return 'driving-traffic';
}

export async function applyConvoyRedirect(args: {
  missionId: number;
  origin: LatLng;
  destination: LatLng;
  profile?: string | null;
}): Promise<void> {
  const profile = routeProfile(args.profile);
  const route = await fetchPrimaryDirections({
    profile,
    coordinates: [args.origin, args.destination],
    alternatives: false,
    exclude: [],
    depart_at: null,
  });
  if (!route) {
    throw new Error('Could not build a route to that point');
  }
  await applyRoute(
    args.missionId,
    {
      label: 'Redirect',
      geometry: route.geometry,
      distance: route.distance,
      duration: route.duration,
      legs: route.legs,
    },
    [],
    'Redirect',
    { reason: 'operator redirect', spoken: REDIRECT_SPOKEN, profile },
  );
  await replaceMissionWaypoints(args.missionId, [
    {
      label: 'Lead',
      latitude: args.origin.latitude,
      longitude: args.origin.longitude,
    },
    {
      label: 'Redirect',
      latitude: args.destination.latitude,
      longitude: args.destination.longitude,
    },
  ]);
}

export function useConvoyRedirect(opts: {
  mission: Mission | null;
  status: MissionRouteStatus | null;
  tracks: MissionDeviceTrack[];
  onApplied?: () => Promise<void> | void;
}) {
  const { mission, status, tracks, onApplied } = opts;
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const missionId = mission?.id;

  useEffect(() => {
    setPicking(false);
    setError(null);
  }, [missionId]);

  useEffect(() => {
    if (!picking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPicking(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picking]);

  const start = useCallback(() => {
    setError(null);
    setPicking(true);
  }, []);

  const cancel = useCallback(() => {
    setPicking(false);
  }, []);

  const applyClick = useCallback(async (longitude: number, latitude: number) => {
    if (!mission || busy) return;
    const origin = convoyOrigin(status, tracks, mission.waypoints);
    if (!origin) {
      setPicking(false);
      setError('Need a convoy position to redirect from');
      return;
    }
    setPicking(false);
    setBusy(true);
    setError(null);
    try {
      await applyConvoyRedirect({
        missionId: mission.id,
        origin,
        destination: { longitude, latitude },
        profile: mission.route_profile,
      });
      try {
        await sendCrewMessage(REDIRECT_SPOKEN, 'general', 'text');
      } catch {
        // Spoken via route_changed; Chat is the same words, not the delivery.
      }
      await onApplied?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not redirect the convoy');
    } finally {
      setBusy(false);
    }
  }, [busy, mission, onApplied, status, tracks]);

  return { picking, busy, error, start, cancel, applyClick };
}
